import { createErrorClass } from '@canvas/error';
import { retryWithBackoff } from '@canvas/retry';

export const LoadImageError = createErrorClass('LoadImageError');

function loadImageNoRetry(imageUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => {
      resolve(image);
    });
    image.addEventListener('error', () => {
      reject(new LoadImageError('Failed to load image'));
    });
    image.src = imageUrl;

    if (process.env.VITE_LOCAL_TUNNEL_ID) {
      image.crossOrigin = '';
    }
  });
}

/**
 * @param params.maxRetries - the maximum number of retries. Use Number.POSITIVE_INFINITY to retry infinitely
 * @param params.retryTimeMs - time to retry, defaults to 500ms
 * @param params.retryBackoff - The factor by which to increase retries, defaults to 1.5
 * @param params.retryMaxMs - the maximum retry time, defaults to 60s
 */
export type LoadImageOptions = {
  maxRetries: number;
  retryTimeMs?: number;
  retryBackoff?: number;
  retryMaxMs?: number;
};

/**
 * Loads an image from the specified url.
 * This has retries built in.
 *
 * @param imageUrl the URL to load the image from
 * @param options an object of options to configure retry behaviour
 * @returns a promise containing an image, or throws LoadImageError
 */
export function loadImage(
  imageUrl: string,
  options: LoadImageOptions,
): Promise<HTMLImageElement> {
  return retryWithBackoff({
    callback: async () => {
      try {
        const image = await loadImageNoRetry(imageUrl);
        return image;
      } catch (err) {
        console.error(`Failed to load image ${imageUrl}`, err);
        throw err;
      }
    },
    ...options,
  });
}

/**
 * Loads an image from a file blob or media source.
 *
 * @param file the blob or media source to load the image from
 * @returns a promise containing an image, or throws LoadImageError
 */
export async function loadImageFromFile(
  file: Blob | MediaSource,
): Promise<HTMLImageElement> {
  const imageUrl = URL.createObjectURL(file);
  try {
    const image = await loadImageNoRetry(imageUrl);
    return image;
  } finally {
    URL.revokeObjectURL(imageUrl);
  }
}
